import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  centsToMoneyString,
  evaluateDrawerHealth,
  parseCountQty,
  sumDenomLines,
  totalDenominationsCents,
  type CashCountMethod,
  type CashDenominationConfig,
  type ChangeAlertSettings,
  type DenominationCounts,
  type DrawerHealthResult,
} from '@fixflow/shared-types';
import { formatCurrency } from '@/lib/format.js';
import { cn } from '@/lib/utils.js';
import { DrawerHealthAlerts } from '@/components/cash/DrawerHealthAlerts.js';

export type DenominationCounterResult = {
  countMethod: CashCountMethod;
  denominations: DenominationCounts;
  totalCents: number;
  totalMoney: number;
  notifyManagers: boolean;
  drawerHealth: DrawerHealthResult | null;
};

type Props = {
  title: string;
  config: CashDenominationConfig;
  trackCoinRolls: boolean;
  changeAlertSettings?: ChangeAlertSettings | null;
  /** When false, hide drop suggestion section is still shown if applicable (open+close). */
  initialCounts?: DenominationCounts;
  confirmLabel: string;
  onConfirm: (result: DenominationCounterResult) => void;
  onCancel?: () => void;
  isPending?: boolean;
  footerExtra?: (totals: {
    totalCents: number;
    countMethod: CashCountMethod;
    health: DrawerHealthResult | null;
  }) => ReactNode;
  disabled?: boolean;
};

function emptyCounts(): DenominationCounts {
  return {};
}

function inputQtyString(counts: DenominationCounts, key: string): string {
  const v = counts[key];
  if (v == null || typeof v !== 'number') return '';
  return String(v);
}

function rollQtyString(counts: DenominationCounts, key: string): string {
  const rolls = counts.rolls;
  if (!rolls || rolls[key] == null) return '';
  return String(rolls[key]);
}

export function DenominationCounter({
  title,
  config,
  trackCoinRolls,
  changeAlertSettings,
  initialCounts,
  confirmLabel,
  onConfirm,
  onCancel,
  isPending,
  footerExtra,
  disabled,
}: Props) {
  const [counts, setCounts] = useState<DenominationCounts>(initialCounts ?? emptyCounts());
  const [countMethod, setCountMethod] = useState<CashCountMethod>('denominations');
  const [manualTotal, setManualTotal] = useState('');
  const [notifyManagers, setNotifyManagers] = useState(true);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const bills = useMemo(() => config.bills.filter((b) => b.enabled), [config.bills]);
  const coins = useMemo(() => config.coins.filter((c) => c.enabled), [config.coins]);

  const fieldKeys = useMemo(() => {
    const keys: Array<{ kind: 'unit' | 'roll'; key: string }> = [];
    for (const b of bills) keys.push({ kind: 'unit', key: b.key });
    for (const c of coins) keys.push({ kind: 'unit', key: c.key });
    if (trackCoinRolls) {
      for (const c of coins) {
        if (config.rollValues[c.key]) keys.push({ kind: 'roll', key: c.key });
      }
    }
    return keys;
  }, [bills, coins, trackCoinRolls, config.rollValues]);

  const billsSum = useMemo(() => sumDenomLines(counts, bills), [counts, bills]);
  const coinsSum = useMemo(() => sumDenomLines(counts, coins), [counts, coins]);
  const rollsCents = useMemo(() => {
    if (!trackCoinRolls || !counts.rolls) return 0;
    let t = 0;
    for (const c of coins) {
      const qty = parseCountQty(counts.rolls?.[c.key]);
      const roll = Math.round(Number(config.rollValues[c.key] ?? 0));
      t += Math.max(0, qty) * roll;
    }
    return t;
  }, [trackCoinRolls, counts.rolls, coins, config.rollValues]);

  const denomTotalCents = useMemo(
    () => totalDenominationsCents(counts, config, trackCoinRolls),
    [counts, config, trackCoinRolls],
  );

  const manualCents = useMemo(() => {
    if (manualTotal.trim() === '') return 0;
    const n = Number(manualTotal);
    if (!Number.isFinite(n) || n < 0) return 0;
    return Math.round(n * 100);
  }, [manualTotal]);

  const totalCents = countMethod === 'denominations' ? denomTotalCents : manualCents;

  const health = useMemo((): DrawerHealthResult | null => {
    if (!changeAlertSettings?.changeAlertsEnabled) return null;
    return evaluateDrawerHealth({
      denominations: countMethod === 'denominations' ? counts : {},
      config,
      trackCoinRolls,
      settings: changeAlertSettings,
      drawerTotalCentsOverride: countMethod === 'manual_total' ? totalCents : null,
    });
  }, [changeAlertSettings, countMethod, counts, config, trackCoinRolls, totalCents]);

  const setUnitQty = useCallback((key: string, raw: string) => {
    if (raw !== '' && raw !== '-' && !/^\d+$/.test(raw)) return;
    setCounts((prev) => {
      const next = { ...prev };
      if (raw === '' || raw === '-') {
        delete next[key];
      } else {
        const qty = parseCountQty(raw);
        next[key] = qty < 0 ? 0 : qty;
      }
      return next;
    });
  }, []);

  const setRollQty = useCallback((key: string, raw: string) => {
    if (raw !== '' && raw !== '-' && !/^\d+$/.test(raw)) return;
    setCounts((prev) => {
      const rolls = { ...(prev.rolls ?? {}) };
      if (raw === '' || raw === '-') {
        delete rolls[key];
      } else {
        const qty = parseCountQty(raw);
        rolls[key] = qty < 0 ? 0 : qty;
      }
      const next: DenominationCounts = { ...prev };
      if (Object.keys(rolls).length) next.rolls = rolls;
      else delete next.rolls;
      return next;
    });
  }, []);

  const focusNext = (index: number) => {
    const next = inputRefs.current[index + 1];
    if (next) {
      next.focus();
      next.select();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      if (event.key === 'Enter') {
        event.preventDefault();
        if (index >= fieldKeys.length - 1) return;
        focusNext(index);
      }
      // Tab: browser moves next; still works with natural tab order since inputs are sequential
    }
  };

  const clearAll = () => {
    setCounts(emptyCounts());
    setManualTotal('');
  };

  const submit = () => {
    onConfirm({
      countMethod,
      denominations: countMethod === 'denominations' ? counts : {},
      totalCents,
      totalMoney: totalCents / 100,
      notifyManagers,
      drawerHealth: health,
    });
  };

  let refIndex = 0;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-lg font-black text-slate-950">{title}</h3>
        {countMethod === 'denominations' ? (
          <button
            type="button"
            className="text-sm font-semibold text-primary underline-offset-2 hover:underline"
            onClick={() => setCountMethod('manual_total')}
          >
            Enter total instead
          </button>
        ) : (
          <button
            type="button"
            className="text-sm font-semibold text-primary underline-offset-2 hover:underline"
            onClick={() => setCountMethod('denominations')}
          >
            Count by denomination
          </button>
        )}
      </div>

      {countMethod === 'manual_total' ? (
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Total counted</span>
          <input
            className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-lg font-semibold"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={manualTotal}
            onChange={(e) => setManualTotal(e.target.value)}
            disabled={disabled}
            autoFocus
          />
          <p className="mt-1 text-xs text-slate-500">
            Recorded as manual total (no per-denomination breakdown).
          </p>
        </label>
      ) : (
        <>
          {bills.length ? (
            <section className="space-y-2">
              <h4 className="text-xs font-black uppercase tracking-wide text-slate-500">Bills</h4>
              <div className="space-y-1.5">
                {bills.map((unit) => {
                  const idx = refIndex++;
                  const qty = parseCountQty(counts[unit.key]);
                  const line = Math.max(0, qty) * unit.valueCents;
                  return (
                    <div
                      key={unit.key}
                      className="grid grid-cols-[5rem_1fr_6.5rem] items-center gap-2 text-sm sm:grid-cols-[6rem_5rem_1fr]"
                    >
                      <span className="font-semibold text-slate-800">{unit.label}</span>
                      <input
                        ref={(el) => {
                          inputRefs.current[idx] = el;
                        }}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-right font-semibold tabular-nums"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={inputQtyString(counts, unit.key)}
                        placeholder="0"
                        onChange={(e) => setUnitQty(unit.key, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, idx)}
                        disabled={disabled}
                      />
                      <span className="text-right tabular-nums text-slate-600">
                        = {centsToMoneyString(line)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end border-t border-slate-200 pt-2 text-sm font-bold">
                <span className="mr-6 text-slate-500">Bills</span>
                <span className="tabular-nums">{centsToMoneyString(billsSum.subtotalCents)}</span>
              </div>
            </section>
          ) : null}

          {coins.length ? (
            <section className="space-y-2">
              <h4 className="text-xs font-black uppercase tracking-wide text-slate-500">Coins</h4>
              <div className="space-y-1.5">
                {coins.map((unit) => {
                  const idx = refIndex++;
                  const qty = parseCountQty(counts[unit.key]);
                  const line = Math.max(0, qty) * unit.valueCents;
                  return (
                    <div
                      key={unit.key}
                      className="grid grid-cols-[5rem_1fr_6.5rem] items-center gap-2 text-sm sm:grid-cols-[6rem_5rem_1fr]"
                    >
                      <span className="font-semibold text-slate-800">{unit.label}</span>
                      <input
                        ref={(el) => {
                          inputRefs.current[idx] = el;
                        }}
                        className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-right font-semibold tabular-nums"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={inputQtyString(counts, unit.key)}
                        placeholder="0"
                        onChange={(e) => setUnitQty(unit.key, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, idx)}
                        disabled={disabled}
                      />
                      <span className="text-right tabular-nums text-slate-600">
                        = {centsToMoneyString(line)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-end border-t border-slate-200 pt-2 text-sm font-bold">
                <span className="mr-6 text-slate-500">Coins</span>
                <span className="tabular-nums">{centsToMoneyString(coinsSum.subtotalCents)}</span>
              </div>
            </section>
          ) : null}

          {trackCoinRolls && coins.length ? (
            <section className="space-y-2">
              <h4 className="text-xs font-black uppercase tracking-wide text-slate-500">
                Coin rolls
              </h4>
              <div className="space-y-1.5">
                {coins
                  .filter((c) => config.rollValues[c.key])
                  .map((unit) => {
                    const idx = refIndex++;
                    const qty = parseCountQty(counts.rolls?.[unit.key]);
                    const roll = Math.round(Number(config.rollValues[unit.key] ?? 0));
                    const line = Math.max(0, qty) * roll;
                    return (
                      <div
                        key={`roll-${unit.key}`}
                        className="grid grid-cols-[5rem_1fr_6.5rem] items-center gap-2 text-sm sm:grid-cols-[6rem_5rem_1fr]"
                      >
                        <span className="font-semibold text-slate-800">{unit.label}</span>
                        <input
                          ref={(el) => {
                            inputRefs.current[idx] = el;
                          }}
                          className="w-full rounded-lg border border-slate-300 px-2 py-1.5 text-right font-semibold tabular-nums"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={rollQtyString(counts, unit.key)}
                          placeholder="0"
                          onChange={(e) => setRollQty(unit.key, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(e, idx)}
                          disabled={disabled}
                        />
                        <span className="text-right tabular-nums text-slate-600">
                          = {centsToMoneyString(line)}
                        </span>
                      </div>
                    );
                  })}
              </div>
              <div className="flex justify-end border-t border-slate-200 pt-2 text-sm font-bold">
                <span className="mr-6 text-slate-500">Rolls</span>
                <span className="tabular-nums">{centsToMoneyString(rollsCents)}</span>
              </div>
            </section>
          ) : null}
        </>
      )}

      <div className="flex justify-between border-t-2 border-slate-900 pt-3 text-lg font-black">
        <span>TOTAL COUNTED</span>
        <span className="tabular-nums">{formatCurrency(totalCents / 100)}</span>
      </div>

      <DrawerHealthAlerts
        health={health}
        notifyManagers={notifyManagers}
        onNotifyManagersChange={setNotifyManagers}
      />

      {footerExtra?.({ totalCents, countMethod, health })}

      <div className="flex flex-wrap justify-between gap-2 pt-1">
        <button
          type="button"
          className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
          onClick={clearAll}
          disabled={disabled || isPending}
        >
          Clear
        </button>
        <div className="flex gap-2">
          {onCancel ? (
            <button
              type="button"
              className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold"
              onClick={onCancel}
              disabled={isPending}
            >
              Cancel
            </button>
          ) : null}
          <button
            type="button"
            className={cn(
              'rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white disabled:opacity-50',
            )}
            onClick={submit}
            disabled={disabled || isPending}
          >
            {isPending ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
